import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";

export default function PricingTab() {
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/pricing").then((r) => setPricing(r.data)); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Deep-numeric clone
      const out = {
        print: {},
        lamination: {},
        stapling: Number(pricing.stapling),
        binding: {},
        cover_colors: pricing.cover_colors || ["clear", "blue", "red", "green"],
        convenience_fee_pct: Number(pricing.convenience_fee_pct),
        convenience_fee_min: Number(pricing.convenience_fee_min),
        tiers: {},
        min_order: Number(pricing.min_order),
      };
      for (const size of Object.keys(pricing.print || {})) {
        out.print[size] = {
          bw: Number(pricing.print[size].bw),
          color: Number(pricing.print[size].color),
        };
      }
      for (const size of Object.keys(pricing.lamination || {})) {
        out.lamination[size] = Number(pricing.lamination[size]);
      }
      for (const k of Object.keys(pricing.binding || {})) {
        out.binding[k] = (pricing.binding[k] || []).map((t) => ({
          min: parseInt(t.min, 10),
          max: parseInt(t.max, 10),
          price: Number(t.price),
        }));
      }
      for (const k of Object.keys(pricing.tiers || {})) {
        out.tiers[k] = {
          ...(pricing.tiers[k] || {}),
          multiplier: Number(pricing.tiers[k].multiplier),
        };
      }
      await api.put("/pricing", out);
      toast.success("Pricing updated");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  const setPrint = (size, color, value) => {
    setPricing({
      ...pricing,
      print: { ...pricing.print, [size]: { ...pricing.print[size], [color]: value } },
    });
  };
  const setLam = (size, value) => {
    setPricing({ ...pricing, lamination: { ...pricing.lamination, [size]: value } });
  };
  const setTier = (kind, idx, key, value) => {
    const tiers = [...(pricing.binding[kind] || [])];
    tiers[idx] = { ...tiers[idx], [key]: value };
    setPricing({ ...pricing, binding: { ...pricing.binding, [kind]: tiers } });
  };
  const addTier = (kind) => {
    const tiers = [...(pricing.binding[kind] || [])];
    const last = tiers[tiers.length - 1] || { min: 0, max: 0, price: 0 };
    tiers.push({ min: Number(last.max) + 1, max: Number(last.max) + 100, price: Number(last.price) + 50 });
    setPricing({ ...pricing, binding: { ...pricing.binding, [kind]: tiers } });
  };
  const removeTier = (kind, idx) => {
    const tiers = (pricing.binding[kind] || []).filter((_, i) => i !== idx);
    setPricing({ ...pricing, binding: { ...pricing.binding, [kind]: tiers } });
  };

  if (!pricing) return <Loader2 className="w-6 h-6 animate-spin text-[#059669]" />;

  const SIZES = ["A4", "A3", "A5"];

  return (
    <div className="space-y-6 max-w-5xl" data-testid={ADMIN.pricingForm}>
      {/* Printing rates */}
      <section className="bg-white border border-[#E5E5E5] p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">§ Printing rates (KES per page)</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
          {SIZES.map((size) => (
            <div key={size} className="bg-white p-4">
              <div className="font-display font-black text-2xl mb-3">{size}</div>
              <label className="block mb-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">B&W</div>
                <input
                  type="number" step="any"
                  value={pricing.print?.[size]?.bw ?? ""}
                  onChange={(e) => setPrint(size, "bw", e.target.value)}
                  data-testid={`pricing-print-${size}-bw`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Color</div>
                <input
                  type="number" step="any"
                  value={pricing.print?.[size]?.color ?? ""}
                  onChange={(e) => setPrint(size, "color", e.target.value)}
                  data-testid={`pricing-print-${size}-color`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Lamination */}
      <section className="bg-white border border-[#E5E5E5] p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">§ Lamination (KES per page, by size)</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {SIZES.map((size) => (
            <label key={size} className="block">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">{size}</div>
              <input
                type="number" step="any"
                value={pricing.lamination?.[size] ?? ""}
                onChange={(e) => setLam(size, e.target.value)}
                data-testid={`pricing-lam-${size}`}
                className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
              />
            </label>
          ))}
        </div>
      </section>

      {/* Stapling + Min order + Convenience fee */}
      <section className="bg-white border border-[#E5E5E5] p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">§ Other static charges</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Stapling (KES per booklet)</div>
            <input
              type="number" step="any"
              value={pricing.stapling ?? ""}
              onChange={(e) => setPricing({ ...pricing, stapling: e.target.value })}
              data-testid="pricing-stapling"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Minimum order (KES)</div>
            <input
              type="number" step="any"
              value={pricing.min_order ?? ""}
              onChange={(e) => setPricing({ ...pricing, min_order: e.target.value })}
              data-testid="pricing-min-order"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Convenience fee % (e.g. 0.01 = 1%)</div>
            <input
              type="number" step="0.001"
              value={pricing.convenience_fee_pct ?? ""}
              onChange={(e) => setPricing({ ...pricing, convenience_fee_pct: e.target.value })}
              data-testid="pricing-fee-pct"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Convenience fee minimum (KES)</div>
            <input
              type="number" step="any"
              value={pricing.convenience_fee_min ?? ""}
              onChange={(e) => setPricing({ ...pricing, convenience_fee_min: e.target.value })}
              data-testid="pricing-fee-min"
              className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
            />
          </label>
        </div>
      </section>

      {/* Priority tiers */}
      <section className="bg-white border border-[#E5E5E5] p-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-4">§ Order priority tiers</div>
        <div className="grid sm:grid-cols-3 gap-px bg-[#E5E5E5] border border-[#E5E5E5]">
          {Object.entries(pricing.tiers || {}).map(([key, t]) => (
            <div key={key} className="bg-white p-4">
              <div className="font-display font-black text-xl mb-2 uppercase">{t.label || key}</div>
              <label className="block mb-2">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Multiplier</div>
                <input
                  type="number" step="0.1"
                  value={t.multiplier ?? 1}
                  onChange={(e) => setPricing({ ...pricing, tiers: { ...pricing.tiers, [key]: { ...t, multiplier: e.target.value } } })}
                  data-testid={`tier-mult-${key}`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
              </label>
              <label className="block">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252] mb-1">Turnaround</div>
                <input
                  value={t.turnaround || ""}
                  onChange={(e) => setPricing({ ...pricing, tiers: { ...pricing.tiers, [key]: { ...t, turnaround: e.target.value } } })}
                  data-testid={`tier-turn-${key}`}
                  className="w-full px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none text-xs"
                />
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Binding tiers */}
      {["spiral", "tape", "hardcover"].map((kind) => (
        <section key={kind} className="bg-white border border-[#E5E5E5] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">§ {kind} binding — by sheet count</div>
            <button
              onClick={() => addTier(kind)}
              data-testid={`add-tier-${kind}`}
              className="text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1.5 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
            >
              + Add tier
            </button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-[#525252]">
              <div className="col-span-3">Min sheets</div>
              <div className="col-span-3">Max sheets</div>
              <div className="col-span-4">Price (KES)</div>
              <div className="col-span-2"></div>
            </div>
            {(pricing.binding?.[kind] || []).map((tier, i) => (
              <div key={`${kind}-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input
                  type="number" min={1}
                  value={tier.min}
                  onChange={(e) => setTier(kind, i, "min", e.target.value)}
                  data-testid={`tier-${kind}-${i}-min`}
                  className="col-span-3 px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
                <input
                  type="number" min={1}
                  value={tier.max}
                  onChange={(e) => setTier(kind, i, "max", e.target.value)}
                  data-testid={`tier-${kind}-${i}-max`}
                  className="col-span-3 px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
                <input
                  type="number" step="any"
                  value={tier.price}
                  onChange={(e) => setTier(kind, i, "price", e.target.value)}
                  data-testid={`tier-${kind}-${i}-price`}
                  className="col-span-4 px-3 py-2 border border-[#E5E5E5] focus:border-[#0A0A0A] outline-none mono"
                />
                <button
                  onClick={() => removeTier(kind, i)}
                  data-testid={`tier-${kind}-${i}-remove`}
                  className="col-span-2 px-3 py-2 border border-[#DC2626] text-[#DC2626] hover:bg-[#DC2626] hover:text-white text-[10px] uppercase tracking-[0.2em] font-bold"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}

      <button
        onClick={save}
        disabled={saving}
        data-testid={ADMIN.saveBtn}
        className="inline-flex items-center gap-2 bg-[#059669] text-white px-6 py-3 text-sm uppercase tracking-[0.2em] font-bold hover:bg-[#047857] disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save pricing
      </button>
    </div>
  );
}
